import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Building2, Users, Mail, Link2, Github, Loader2, Trash2, Plus, Send } from 'lucide-react';
import { toast } from 'sonner';
import { apiClient } from '@/lib/api-client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card } from '@/components/ui/card';
import { useOrgContext } from '@/providers/org-provider';
import { cn } from '@/lib/utils';
import { ProvidersTab } from '@/components/org-settings/providers-tab';
import { TepTab } from '@/components/org-settings/tep-tab';

/**
 * Org settings page. Tabs:
 *   - General: name, kind badge, domain (team-only)
 *   - Members: list, role change, remove
 *   - Invitations: pending invites, create, cancel
 *   - Integrations: team-only — GitHub App, domain capture
 */
type Tab = 'general' | 'members' | 'invitations' | 'providers' | 'tep' | 'integrations';

export function OrgSettingsPage() {
  const { orgId } = useParams<{ orgId: string }>();
  const { currentOrg } = useOrgContext();
  const initialTab = (() => {
    const t = new URLSearchParams(window.location.search).get('tab');
    if (t === 'general' || t === 'members' || t === 'invitations' || t === 'providers' || t === 'tep' || t === 'integrations') return t;
    return 'general';
  })();
  const [tab, setTab] = useState<Tab>(initialTab as Tab);

  if (!orgId || !currentOrg) {
    return <div className="p-8 text-center text-muted-foreground">Loading…</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <div className="h-10 w-10 rounded-xl bg-blue-100 dark:bg-blue-900/40 flex items-center justify-center">
          <Building2 className="h-5 w-5 text-blue-600 dark:text-blue-400" />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-foreground">{currentOrg.name}</h1>
          <p className="text-sm text-muted-foreground capitalize">{currentOrg.kind} org · You are {currentOrg.role}</p>
        </div>
      </div>

      <div className="border-b border-border flex gap-6">
        {(['general', 'members', 'invitations', 'providers', 'tep', ...(currentOrg.kind === 'team' ? ['integrations'] as const : [])] as Tab[]).map((t) => (
          <button
            key={t}
            onClick={() => setTab(t)}
            className={cn(
              'pb-2 text-sm font-medium border-b-2 capitalize',
              tab === t ? 'border-foreground text-foreground' : 'border-transparent text-muted-foreground hover:text-foreground',
            )}
          >
            {t === 'tep' ? 'Compliance Posture' : t}
          </button>
        ))}
      </div>

      {tab === 'general' && <GeneralTab orgId={orgId} kind={currentOrg.kind} />}
      {tab === 'members' && <MembersTab orgId={orgId} kind={currentOrg.kind} />}
      {tab === 'invitations' && <InvitationsTab orgId={orgId} kind={currentOrg.kind} />}
      {tab === 'providers' && <ProvidersTab orgId={orgId} />}
      {tab === 'tep' && <TepTab orgId={orgId} />}
      {tab === 'integrations' && currentOrg.kind === 'team' && <IntegrationsTab orgId={orgId} />}
    </div>
  );
}

function GeneralTab({ orgId, kind }: { orgId: string; kind: 'personal' | 'team' }) {
  const queryClient = useQueryClient();
  const { data } = useQuery({ queryKey: ['org', orgId], queryFn: () => apiClient.getOrg(orgId) });
  const org = data?.data as { name?: string; domain?: string | null } | undefined;
  const [name, setName] = useState('');
  const [domain, setDomain] = useState('');

  useEffect(() => {
    if (org) {
      setName(org.name ?? '');
      setDomain(org.domain ?? '');
    }
  }, [org]);

  const update = useMutation({
    mutationFn: () => apiClient.updateOrg(orgId, { name, ...(kind === 'team' ? { domain: domain.trim() || null } : {}) }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['org', orgId] });
      queryClient.invalidateQueries({ queryKey: ['me', 'orgs'] });
    },
  });

  return (
    <Card className="p-6 space-y-4 max-w-xl">
      <div>
        <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-1 block">Name</label>
        <Input value={name} onChange={(e) => setName(e.target.value)} />
      </div>
      {kind === 'team' && (
        <div>
          <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-1 block">Email domain</label>
          <Input value={domain} onChange={(e) => setDomain(e.target.value)} placeholder="example.com" />
        </div>
      )}
      <Button onClick={() => update.mutate()} disabled={update.isPending}>
        {update.isPending ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
        Save
      </Button>
    </Card>
  );
}

interface MemberRow { userId: string; role: string; email: string | null; displayName: string | null; }

function MembersTab({ orgId, kind }: { orgId: string; kind: 'personal' | 'team' }) {
  const queryClient = useQueryClient();
  const { data, isLoading } = useQuery({ queryKey: ['org', orgId, 'members'], queryFn: () => apiClient.listOrgMembers(orgId) });
  const members = (data?.data as MemberRow[] | undefined) ?? [];

  const updateRole = useMutation({
    mutationFn: (vars: { userId: string; role: string }) => apiClient.updateOrgMember(orgId, vars.userId, vars.role),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['org', orgId, 'members'] }),
  });
  const remove = useMutation({
    mutationFn: (userId: string) => apiClient.removeOrgMember(orgId, userId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['org', orgId, 'members'] }),
  });

  if (isLoading) return <p className="text-sm text-muted-foreground">Loading…</p>;

  return (
    <Card className="p-0 overflow-hidden">
      <div className="px-5 py-3 border-b border-border flex items-center gap-2">
        <Users className="h-4 w-4 text-muted-foreground" />
        <p className="text-sm font-medium">{members.length} {members.length === 1 ? 'member' : 'members'}</p>
      </div>
      <div className="divide-y divide-border">
        {members.map((m) => (
          <div key={m.userId} className="flex items-center justify-between px-5 py-3 gap-4">
            <div className="min-w-0">
              <p className="text-sm font-medium truncate">{m.displayName || m.email || m.userId}</p>
              <p className="text-xs text-muted-foreground truncate">{m.email}</p>
            </div>
            <div className="flex items-center gap-3 shrink-0">
              {kind === 'team' ? (
                <select
                  value={m.role}
                  onChange={(e) => updateRole.mutate({ userId: m.userId, role: e.target.value })}
                  className="text-xs border border-border rounded px-2 py-1 bg-background"
                >
                  <option value="admin">admin</option>
                  <option value="member">member</option>
                  <option value="viewer">viewer</option>
                </select>
              ) : (
                <span className="text-xs text-muted-foreground capitalize">{m.role}</span>
              )}
              {kind === 'team' && (
                <button
                  onClick={() => { if (confirm('Remove this member?')) remove.mutate(m.userId); }}
                  className="text-muted-foreground hover:text-red-600"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              )}
            </div>
          </div>
        ))}
      </div>
    </Card>
  );
}

interface InviteRow { inviteId: string; token: string; email: string; role: string; scope: string; expiresAt: string; acceptedAt: string | null; }

function InvitationsTab({ orgId, kind }: { orgId: string; kind: 'personal' | 'team' }) {
  const queryClient = useQueryClient();
  const { data } = useQuery({ queryKey: ['org', orgId, 'invitations'], queryFn: () => apiClient.listInvitations(orgId) });
  const invites = (data?.data as InviteRow[] | undefined) ?? [];

  const [email, setEmail] = useState('');
  const [role, setRole] = useState<'admin' | 'member' | 'viewer'>('member');

  const create = useMutation({
    mutationFn: () => apiClient.createInvitation(orgId, { email, role, scope: 'org' }),
    onSuccess: (res) => {
      const data = res.data as { emailSent?: boolean; email?: string } | undefined;
      if (data?.emailSent) {
        toast.success(`Invitation email sent to ${data.email}`);
      } else {
        toast.warning(
          `Invite created for ${data?.email ?? 'recipient'}, but the email didn't send. Use "copy link" to share manually.`,
        );
      }
      setEmail('');
      queryClient.invalidateQueries({ queryKey: ['org', orgId, 'invitations'] });
    },
    onError: (err: Error) => toast.error(err.message),
  });
  const cancel = useMutation({
    mutationFn: (inviteId: string) => apiClient.cancelInvitation(orgId, inviteId),
    onSuccess: () => {
      toast.success('Invitation cancelled');
      queryClient.invalidateQueries({ queryKey: ['org', orgId, 'invitations'] });
    },
    onError: (err: Error) => toast.error(err.message),
  });
  const resend = useMutation({
    mutationFn: (inviteId: string) => apiClient.resendInvitation(orgId, inviteId),
    onSuccess: (res, inviteId) => {
      const data = res.data as { sent?: boolean; email?: string } | undefined;
      if (data?.sent) {
        toast.success(`Invitation re-sent to ${data.email}`);
      } else {
        toast.error('Email send failed. The invite link is still valid — share it directly.');
      }
      // Touch the cache so any UI bound to per-invite state refreshes
      // (no fields change today, but adds a hook for future "lastSentAt").
      queryClient.invalidateQueries({ queryKey: ['org', orgId, 'invitations'] });
      void inviteId;
    },
    onError: (err: Error) => toast.error(err.message),
  });

  if (kind === 'personal') {
    return (
      <Card className="p-6 text-sm text-muted-foreground">
        Personal orgs can't have additional members. Create a team org to invite collaborators.
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      <Card className="p-5">
        <p className="text-sm font-semibold mb-3">Invite a teammate</p>
        <div className="flex items-stretch gap-2 flex-wrap">
          <Input
            type="email"
            placeholder="teammate@example.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="flex-1 min-w-[220px]"
          />
          <select
            value={role}
            onChange={(e) => setRole(e.target.value as typeof role)}
            className="border border-border rounded-md px-3 py-2 bg-background text-sm"
          >
            <option value="admin">admin</option>
            <option value="member">member</option>
            <option value="viewer">viewer</option>
          </select>
          <Button disabled={!email.trim() || create.isPending} onClick={() => create.mutate()}>
            {create.isPending ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Plus className="h-4 w-4 mr-2" />}
            Send invite
          </Button>
        </div>
        {create.isError && (
          <p className="text-xs text-red-600 mt-2">{(create.error as Error).message}</p>
        )}
        {create.isSuccess && (
          <p className="text-xs text-muted-foreground mt-3">
            Share this link with the invitee:
            <code className="ml-2 px-2 py-0.5 bg-muted rounded text-foreground">
              {window.location.origin}/accept/{(create.data?.data as { token?: string } | undefined)?.token ?? ''}
            </code>
          </p>
        )}
      </Card>

      <Card className="p-0 overflow-hidden">
        <div className="px-5 py-3 border-b border-border flex items-center gap-2">
          <Mail className="h-4 w-4 text-muted-foreground" />
          <p className="text-sm font-medium">Pending invitations ({invites.filter((i) => !i.acceptedAt).length})</p>
        </div>
        <div className="divide-y divide-border">
          {invites.length === 0 && (
            <p className="px-5 py-6 text-sm text-muted-foreground text-center">No invitations yet.</p>
          )}
          {invites.map((inv) => (
            <div key={inv.inviteId} className="flex items-center justify-between px-5 py-3 gap-4">
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium truncate">{inv.email}</p>
                <p className="text-xs text-muted-foreground">
                  {inv.role} · {inv.acceptedAt ? `accepted ${new Date(inv.acceptedAt).toLocaleDateString()}` : `expires ${new Date(inv.expiresAt).toLocaleDateString()}`}
                </p>
              </div>
              <div className="flex items-center gap-3">
                {!inv.acceptedAt && (
                  <>
                    <button
                      onClick={() => resend.mutate(inv.inviteId)}
                      disabled={resend.isPending && resend.variables === inv.inviteId}
                      className="text-xs text-muted-foreground hover:text-foreground inline-flex items-center gap-1 disabled:opacity-50"
                      title="Resend invitation email"
                    >
                      {resend.isPending && resend.variables === inv.inviteId ? (
                        <Loader2 className="h-3.5 w-3.5 animate-spin" />
                      ) : (
                        <Send className="h-3.5 w-3.5" />
                      )}
                      resend
                    </button>
                    <button
                      onClick={() => {
                        navigator.clipboard.writeText(`${window.location.origin}/accept/${inv.token}`);
                        toast.success('Invite link copied');
                      }}
                      className="text-xs text-muted-foreground hover:text-foreground inline-flex items-center gap-1"
                    >
                      <Link2 className="h-3.5 w-3.5" /> copy link
                    </button>
                  </>
                )}
                <button
                  onClick={() => {
                    const verb = inv.acceptedAt ? 'remove' : 'cancel';
                    if (confirm(`Are you sure you want to ${verb} the invitation for ${inv.email}?`)) {
                      cancel.mutate(inv.inviteId);
                    }
                  }}
                  className="text-muted-foreground hover:text-red-600"
                  title={inv.acceptedAt ? 'Remove invitation record' : 'Cancel invitation'}
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
}

function IntegrationsTab({ orgId }: { orgId: string }) {
  const queryClient = useQueryClient();
  type Installation = {
    installationId: string;
    accountLogin: string;
    accountType: string;
    accountId: number;
    repositorySelection: 'all' | 'selected' | null;
    addedBy: string;
    addedAt: string;
  };

  const installations = useQuery({
    queryKey: ['github', 'installations', orgId],
    queryFn: () => apiClient.listGithubInstallations(orgId),
  });
  const rows = ((installations.data?.data as Installation[] | undefined) ?? []);

  const startInstall = useMutation({
    mutationFn: () => apiClient.getGithubInstallUrl(orgId),
    onSuccess: (res) => {
      const url = (res.data as { url?: string } | undefined)?.url;
      if (url) {
        window.open(url, '_blank', 'noopener,noreferrer');
        toast.info('GitHub install opened in new tab. Refresh this page once you finish.');
      } else {
        toast.error('No install URL returned.');
      }
    },
    onError: (err) => toast.error(`Could not start install: ${(err as Error).message}`),
  });

  const remove = useMutation({
    mutationFn: (installationId: string) => apiClient.deleteGithubInstallation(orgId, installationId),
    onSuccess: () => {
      toast.success('Installation unlinked. Remove it from GitHub too to fully revoke access.');
      queryClient.invalidateQueries({ queryKey: ['github', 'installations', orgId] });
    },
    onError: (err) => toast.error(`Could not unlink: ${(err as Error).message}`),
  });

  // ?ok=1 / ?error=… come back from the GitHub callback redirect.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('ok') === '1') {
      toast.success('GitHub App installed.');
      queryClient.invalidateQueries({ queryKey: ['github', 'installations', orgId] });
      window.history.replaceState({}, '', window.location.pathname);
    }
    const err = params.get('error');
    if (err) {
      toast.error(`GitHub install failed: ${err}`);
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, [orgId, queryClient]);

  return (
    <Card className="p-6 space-y-6">
      <div className="flex items-start gap-3">
        <Github className="h-6 w-6 text-foreground mt-1" />
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-3">
            <div>
              <p className="text-sm font-semibold">GitHub App</p>
              <p className="text-xs text-muted-foreground">
                Install the Trinetra GitHub App on each GitHub org you want to scan private repos from.
                You can link multiple installations (e.g. personal + work).
              </p>
            </div>
            <Button size="sm" onClick={() => startInstall.mutate()} disabled={startInstall.isPending}>
              {startInstall.isPending ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
              Install
            </Button>
          </div>

          <div className="mt-4 space-y-2">
            {installations.isLoading && (
              <p className="text-xs text-muted-foreground">loading installations…</p>
            )}
            {!installations.isLoading && rows.length === 0 && (
              <p className="text-xs text-muted-foreground">
                No installations yet. Install the App to pick private repos when creating a project.
              </p>
            )}
            {rows.map((r) => (
              <div key={r.installationId} className="flex items-center justify-between gap-3 rounded-md border border-border bg-card px-3 py-2">
                <div className="min-w-0">
                  <p className="text-sm font-medium truncate">
                    {r.accountLogin} <span className="text-[11px] text-muted-foreground">({r.accountType})</span>
                  </p>
                  <p className="text-[11px] text-muted-foreground">
                    {r.repositorySelection === 'all' ? 'All repos' : 'Selected repos'} · install id <code>{r.installationId}</code>
                  </p>
                </div>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => {
                    if (confirm(`Unlink ${r.accountLogin}? This stops Trinetra from cloning its repos. You'll also need to uninstall on GitHub to fully revoke.`)) {
                      remove.mutate(r.installationId);
                    }
                  }}
                  disabled={remove.isPending}
                >
                  <Trash2 className="h-3.5 w-3.5 mr-1" />
                  Unlink
                </Button>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="flex items-start gap-3 pt-4 border-t border-border">
        <Building2 className="h-6 w-6 text-foreground" />
        <div className="flex-1">
          <p className="text-sm font-semibold">Domain capture</p>
          <p className="text-xs text-muted-foreground mb-2">
            Automatically add new sign-ups with a matching email domain to this org as members.
          </p>
          <Button variant="outline" size="sm" disabled>
            Configure — coming soon
          </Button>
        </div>
      </div>
    </Card>
  );
}
